import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {Modal, ModalHeader, ModalBody} from "../../../../components/Modal";
import DotsPopup from "../../../../components/Popup/DotsPopup";
import { CalendarWeek as CalendarIcon, Clock } from "flowbite-react-icons/outline";
import { Meeting } from "../../interfaces";
import {
  addMinutes,
  dateFormatHourMinute,
  zDate,
} from "../../../../helpers/dateFunctions";
import AlertIcon from "../../../../components/Icons/AlertIcon";
import Api from "../../../../services/Api";
import { openMeeting } from "../../../../helpers/open_meeting";

interface Props {
  meetingData: Array<Meeting>;
  isPreviousMeetings: boolean;
  onCancellation: Function;
  animate: boolean;
}

const ListComponent = ({
  meetingData,
  isPreviousMeetings,
  onCancellation,
  animate = false,
}: Props) => {
  const api = Api.getInstance();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = t("lang");

  // Ref to access the list element
  const listRef = useRef<HTMLUListElement | null>(null);
  // State to store the dynamic max-height
  const [maxHeight, setMaxHeight] = useState(0);
  const [showLateChange, setShowLateChange] = useState<Meeting | null>(null);

  useEffect(() => {
    if (listRef.current) {
      // Update maxHeight whenever meetingData changes
      setMaxHeight(listRef.current.scrollHeight);
    }
  }, [meetingData]);

  const onChangeMeeting = (meeting: Meeting) => {
    const secondsToStart =
      (meeting.startDate.getTime() - new Date().getTime()) / 1000;
    if (secondsToStart < 24 * 3600) {
      setShowLateChange(meeting);
    } else {
      navigate(`/videocoach/book-meeting?meeting_id=${meeting.id}`);
    }
  };

  const goToMeeting = (meetingId: string) => {
    if (api.apiToken) {
      openMeeting(meetingId, api.apiToken, "client");
    } else {
      alert("Error - missing api token");
    }
  };

  return (
    <>
      <div
        className={` ${animate ? "transition-[max-height] duration-500" : ""}`}
        style={{ maxHeight: `${maxHeight}px` }}
      >
        <ul ref={listRef} className="w-full flex flex-col">
          {meetingData.map((meeting, index) => {
            let popItems: Array<any>;
            if (isPreviousMeetings) {
              popItems = [
                {
                  title: t("to-meeting"),
                  click: () => goToMeeting(meeting.id),
                },
              ];
            } else {
              popItems = [
                {
                  title: t("to-meeting"),
                  click: () => goToMeeting(meeting.id),
                },
                {
                  title: t("change-meeting"),
                  click: () => onChangeMeeting(meeting),
                },
                {
                  title: t("cancel-meeting"),
                  click: () => onCancellation(meeting),
                },
              ];
            }
            return (
              <li
                key={index}
                className="flex items-center justify-between z-title3 text-white bg-transparent"
              >
                {/* Left Group */}
                <div className="flex items-center">
                  {/* Calendar Icon */}
                  <CalendarIcon className="w-6 h-6" aria-hidden="true" />
                  {/* Date Text with Fixed Width */}
                  <p className="ml-2 truncate w-14">
                    {zDate(meeting.startDate, lang, false)}
                  </p>
                  {/* Clock Icon */}
                  <Clock className="ml-2 w-6 h-6" aria-hidden="true" />
                  {/* Time Text */}
                  <p className="ml-2">
                    {dateFormatHourMinute(meeting.startDate)}
                  </p>
                  &ndash;
                  <p>
                    {dateFormatHourMinute(
                      addMinutes(meeting.startDate, meeting.length)
                    )}
                  </p>
                </div>
                {/* Dots Icon and Dropdown */}
                <DotsPopup items={popItems} />
              </li>
            );
          })}
        </ul>
      </div>
      {showLateChange && (
        <Modal isOpen={true} onClose={() => setShowLateChange(null)}>
          <ModalHeader onClose={() => setShowLateChange(null)}>
            <div className="z-title1">
              {lang === "sv" ? "Sen ändring" : "Late cancellation"}
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col justify-between ">
              <div className="flex flex-row justify-center">
                <AlertIcon aria-label="Cancel meeting" className="w-12 h-12 mt-3 text-cherry-500!" />
              </div>
              <p className="mt-6">
                {lang === "sv"
                  ? "Då det är mindre än 24 timmar kvar till mötet så går det tyvärr inte att göra några ändringar."
                  : "As it is less than 24 hours before the meeting starts, you can't do any changes."}
              </p>
              <p className="mt-2">
                {lang === "sv"
                  ? "Om du inte kan ta mötet får du avboka det istället."
                  : "If you can't have the meeting, please cancel it instead."}
              </p>
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default ListComponent;
